import React, { useEffect, useState } from 'react';
import {
	RavenBadge,
	RavenButton,
	RavenInputField,
	RavenModal,
	RavenNumberFormat,
} from '@ravenpay/raven-bank-ui';
import '../../styles/singlesettlement.css';
import '../../../../settlements/styles/settlementModal.css';
import BadgeComponent from '../../../../../../../components/common/badge/BadgeComponent';
import * as XLSX from 'xlsx';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import RootState from '../../../../../../../redux/types';
import { bankboxAPI } from '../../../../../../../redux/bankbox';
import {
	trimLongString,
} from '../../../../../../../utils/helper/Helper';

interface CardData {
	'Wallet ID': string;
	'Card PAN': string;
	CVV: string;
	'Expiry Date': string;
	'Default PIN': string;
	Color: string;
}

const expectedKeys = [
	'Wallet ID',
	'Card PAN',
	'CVV',
	'Expiry Date',
	'Default PIN',
	'Color',
];

interface ComponentProps {
	amount?: number;
	account_name: string;
	accountNumber?: string;
	bank?: string | React.ReactNode;
	type?: string;
	account_details?: string;
	status?: React.ReactNode;
	date?: Date;
	reference: string;
	trx_ref: string;
	isLoadingDetails?: boolean;
	transactionType: string;
	singleObj?: any;
	visible?: boolean;
	onClose: Function;
	singleChild?: any;
	meta_data?: any;
	// buttonColor?: any;
}

const SettlementModal: React.FC<ComponentProps> = ({
	amount,
	account_name,
	// buttonColor,
	transactionType,
	accountNumber,
	bank,
	isLoadingDetails = false,
	type,
	status,
	date,
	trx_ref,
	onClose,
	visible,
}) => {
	const [showDisputeModal, setShowDisputeModal] = useState(false);
	const [cardQueue, setCardQueue] = React.useState<CardData[]>([]);
	const [uploadValue, setUploadValue] = useState<any>();
	const [cardJson, setCardJson] = useState<CardData[]>([]);
	const [description, setDescription] = useState('');
	const [note, setNote] = useState('');

	const [shareModalVisible, setShareModalVisible] = useState(false);
	const [modalType, setModalType] = useState<'settlement' | 'dispute'>(
		'settlement'
	);

	const { loading } = useSelector((state: RootState) => state.bankbox);

	const dispatch = useDispatch();
	const [showSkeleton, setShowSkeleton] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowSkeleton(false);
		}, 5000);

		return () => clearTimeout(timer);
	}, [isLoadingDetails]);

	const [formData, setFormData] = React.useState({
		fname: '',
		lname: '',
		bvn: '',
		nin: '',
		email: '',
		phone: '',
		note: '',
		amount: '',
		reference: '',
		transactionType: '',
		device_label: '',
		delivery_location: '',
		delivery_option: '',
		delivery_address: '',
		delivery_lga: '',
		transaction_id: '',
		customer_phone: '',
		delivery_state: '',
		landmark: '',
		purpose: '',
		type: '',
		id: '',
		customer_name: '',
		region: { value: undefined, label: undefined },
		number_of_devices: 0,
		business_address: '',
		fee_category: { value: undefined, label: undefined } as any,
		bank: { value: undefined, label: undefined } as any,
		bank_code: '',
		account_number: '',
		bank_name: '',
		quantity: '',
		status: 'Pending',
		created_at: new Date().toISOString(),
		account_name: '',
		settlement_date: '',
		daily_payout_time: '00:00',
	});

	function filterEmptyColumns(obj: Record<string, any>) {
		const alwaysKeep = ['note'];
		return Object.fromEntries(
			Object.entries(obj).filter(
				([key, val]) =>
					alwaysKeep.includes(key) || (val !== '---' && val !== '---')
			)
		);
	}

	const buttonColor = 'white-light';

	const handleSubmit = async () => {
		// First, parse the amount and handle empty or invalid values
		const cleanedAmount = amount
			? parseFloat(amount.toString().replace(/[^\d.-]/g, '')) // Convert `amount` to string before calling `replace`
			: 0;

		const val = {
			...formData,
			reference: trx_ref,
			device_label: formData.device_label as unknown as string,
			region: formData.region.label as unknown as string,
			fee_ref: String(formData?.fee_category?.reference),
			bank_name: formData?.bank?.label,
			bank_code: formData?.bank?.value,
			account_number: formData?.account_number,
			account_name: formData?.account_name,
			email: formData.email,
			quantity: formData.number_of_devices,
			business_name: formData.device_label,
			phone: formData.phone,
			amount: cleanedAmount,
			status: 'Pending',
			created_at: new Date().toISOString(),
			transaction_id: formData.id,
			customer_name: `${formData.fname} ${formData.lname}`,
			customer_phone: formData.phone,
			type: transactionType,
			note: formData.note,
		};

		delete val.fee_category;
		delete val.bank;

		try {
			const resp = await dispatch(
				bankboxAPI.raiseDispute(filterEmptyColumns(val) as any)
			);

			//('API response:', resp);
			//('Payload before dispatch:', val);

			if (resp.payload?.status === 'success') {
				await dispatch(
					bankboxAPI.getAllBankBoxRequest({
						per_page: 20,
						page: 1,
					})
				);
			} else {
				console.error('Dispute submission failed:', resp.payload?.message);
			}
		} catch (error) {
			console.error('Error submitting dispute:', error);
		}
	};

	const isFormValid = () => {
		return note.trim() !== '';
	};

	const handleClose = () => {
		// Reset form data and modal state
		setFormData({
			fname: '',
			lname: '',
			reference: '',
			bvn: '',
			nin: '',
			email: '',
			phone: '',
			note: '',
			amount: '',
			transactionType: '',
			device_label: '',
			delivery_location: '',
			delivery_option: '',
			delivery_address: '',
			delivery_lga: '',
			transaction_id: '',
			customer_phone: '',
			delivery_state: '',
			landmark: '',
			purpose: '',
			type: '',
			id: '',
			customer_name: '',
			region: { value: undefined, label: undefined },
			number_of_devices: 0,
			business_address: '',
			fee_category: { value: undefined, label: undefined } as any,
			bank: { value: undefined, label: undefined } as any,
			bank_code: '',
			account_number: '',
			bank_name: '',
			quantity: '',
			status: 'Pending',
			created_at: new Date().toISOString(),
			account_name: '',
			settlement_date: '',
			daily_payout_time: '00:00',
		});

		// Reset other relevant states (like uploadValue, cardJson, etc.)
		setUploadValue(undefined);
		setCardJson([]);
		setCardQueue([]);
		setNote('');
		setDescription('');
		setModalType('settlement'); // Reset modal type to 'settlement'

		// Reset dispute modal state if needed
		setShowDisputeModal(false); // Make sure this is reset

		// Call onClose to handle modal visibility in the parent
		onClose();
	};


	const renderDisputeModalContent = () => (
		<div
			onClick={() => {
				//(singleObj);
			}}
			className="transaction-details-modal details-modal-wrap"
		>
			<h5 style={{ fontSize: '20px', marginBottom: '16px' }}>Dispute</h5>
			<div
				style={{
					backgroundColor: 'rgba(247, 247, 247, 1)',
					padding: '2rem',
					borderRadius: '1rem',
					marginBottom: '2rem',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						marginBottom: '1.2rem',
						alignItems: 'center',
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							marginBottom: '1.2rem',
							alignItems: 'center',
						}}
					>
						<span style={{ fontSize: '1.4rem', fontWeight: '400' }}>
							Reference •
							<>
								<span style={{ marginLeft: '.5rem' }}></span>
							</>
						</span>
						{showSkeleton && isLoadingDetails ? (
							<Skeleton width={100} height={20} />
						) : (
							<span style={{ fontSize: '1.4rem', fontWeight: '400' }}>
								{trimLongString(trx_ref, 15)}
							</span>
						)}
					</div>
					<RavenButton
						color="deep-green-dark"
						label="Back to Transaction"
						style={{
							outline: 'none',
							fontSize: '1.1rem',
							border: 'none',
							padding: '1rem',
							borderRadius: '2rem',
							cursor: 'pointer',
						}}
						onClick={() => {
							setModalType('settlement');
							setShowDisputeModal(false);
						}}
					></RavenButton>
				</div>
				<div
					style={{
						marginBottom: '1.2rem',
						fontSize: '1.4rem',
						fontWeight: '500',
					}}
				>
					{showSkeleton && isLoadingDetails ? (
						<Skeleton width={120} height={20} /> // Skeleton for the amount
					) : (
						<>
							₦{amount?.toLocaleString()} •{' '}
							<span style={{ color: 'gray' }}>{transactionType || '---'}</span>
						</>
					)}
				</div>
			</div>
			<div style={{ marginBottom: '1.2rem' }}>
				<RavenInputField
					color="black-light"
					label="Discription*"
					labelClassName="my_custom_class"
					labelColor="purple-light"
					onActionClick={function noRefCheck() {}}
					textareaColumn={30}
					textareaRow={5}
					type="textarea"
					value={note}
					style={{ marginBottom: '1rem' }}
					onChange={(e: any) => {
						setNote(e.target.value);
						setFormData((prev) => ({ ...prev, note: e.target.value }));
					}}
					placeholder="Write your thoughts here..."
				/>
			</div>
			{/* File Upload */}
			<div
				className="form-section"
				style={{ marginBottom: '3.5rem', marginTop: '3rem' }}
			>
				<RavenInputField
					label="Upload a file (optional)"
					type="upload"
					color="purple-light"
					onChange={(e: any) => {
						//(e);
					}}
				/>
				{!uploadValue && (
					<p style={{ marginTop: '1rem' }}>
						Maximum file size acceptable for upload is 2MB
					</p>
				)}
			</div>
			<RavenButton
				loading={loading}
				onClick={handleSubmit}
				disabled={!isFormValid() || !note}
				color="deep-green-light"
				label="Raise Dispute"
				style={{
					width: '100%',
				}}
			/>
		</div>
	);

	const renderSettlementDetails = () => (
		<>
			<div className="settlement_modal_general_wrapper">
				<p className="settlement_details_modal_header">Settlement Details</p>
				<div className="settlement_details_modal_contents_box">
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Amount
						</p>
						<p className="settlement_details_modal_contents_content_details">
							{RavenNumberFormat(amount)}
						</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Account Name
						</p>
						<p className="settlement_details_modal_contents_content_details">
							{account_name}
						</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Account No.
						</p>
						<p>{accountNumber}</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Bank
						</p>
						<p>{bank}</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Type
						</p>
						<p className="settlement_details_modal_contents_content_details">
							{type}
						</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Status
						</p>
						<p className="settlement_details_modal_contents_content_details">
							{status}
						</p>
					</div>
					<div className="settlement_details_modal_contents_content_holder settle">
						<p className="settlement_details_modal_contents_content_head">
							Date
						</p>
						<p className="settlement_details_modal_contents_content_details">
							{date ? date.toLocaleDateString() : 'N/A'}
						</p>
					</div>
				</div>
			</div>

			<div
				style={{
					width: '100%',
					display: 'flex',
					justifyContent: 'space-between',
					gap: '1rem',
					marginTop: '2rem',
				}}
			>
				<RavenButton
					color={buttonColor}
					label="Raise Dispute"
					loading={false}
					className="reusable-btn-outline-classname"
					// size="big"
					onClick={() => setShowDisputeModal(true)}
					style={{
						width: '100%',
					}}
				/>
				<RavenButton
					color="deep-green-dark"
					label="Pay Settlement"
					loading={false}
					// size="big"
					onClick={() => 'Settlement Paid'}
					style={{
						width: '100%',
						// padding: '1rem 2rem',
						// display: 'inline-flex',
					}}
				/>
			</div>
		</>
	);

	return (
		<RavenModal
			btnColor="deep-green-light"
			btnLabel=""
			effect="fadeInRight"
			visble={visible}
			onClose={handleClose}
			onBtnClick={() => {}}
			className=""
		>
			{showDisputeModal
				? renderDisputeModalContent()
				: renderSettlementDetails()}
		</RavenModal>
	);
};

export default SettlementModal;

// Utility functions
